import React from "react";
import { Box, Button, IconButton, Stack, Tooltip } from "@mui/material";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  resolveDashboardAreaNavigationTarget,
  resolveDashboardPrimaryAreas,
} from "../../utils/dashboardNavigation";
import {
  loadProjectNavigationState,
  markProjectNavigationAreaExplored,
  persistProjectNavigationExploration,
  projectNavigationAreaExplored,
  projectNavigationKey,
  setProjectNavigationExpanded,
  splitProjectNavigationAreas,
  usesProgressiveProjectNavigation,
} from "../../utils/progressiveNavigation";
import DashboardNavigationIcon from "./DashboardNavigationIcon";

const ACTIVE = "#F35A3A";
const INK = "var(--wp-panel-text-primary, #19242F)";

export default function DashboardTopAreaNavigation({
  navigation,
  project = null,
  superAdmin = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const projectKey = projectNavigationKey(project);
  const userKey = String(user?.id || user?._id || user?.email || "").trim().toLowerCase();
  const [progressiveState, setProgressiveState] = React.useState(() =>
    loadProjectNavigationState(project, user),
  );
  const areas = React.useMemo(
    () => resolveDashboardPrimaryAreas(navigation),
    [navigation],
  );
  const progressiveEnabled = Boolean(
    navigation?.route?.isProject &&
      usesProgressiveProjectNavigation(project, { superAdmin }),
  );

  React.useEffect(() => {
    setProgressiveState(loadProjectNavigationState(project, user));
  }, [projectKey, userKey, project, user]);

  React.useEffect(() => {
    const activeAreaKey = navigation?.activeAreaKey;
    if (
      !progressiveEnabled ||
      !activeAreaKey ||
      projectNavigationAreaExplored(progressiveState, activeAreaKey)
    ) return;

    setProgressiveState((current) =>
      markProjectNavigationAreaExplored(project, activeAreaKey, current, user),
    );
    persistProjectNavigationExploration(project, { areaKey: activeAreaKey }).catch(() => {});
  }, [
    progressiveEnabled,
    projectKey,
    project,
    user,
    navigation?.activeAreaKey,
    progressiveState,
  ]);

  const progressive = React.useMemo(
    () => splitProjectNavigationAreas(project, areas, progressiveState, progressiveEnabled),
    [project, areas, progressiveState, progressiveEnabled],
  );
  const neededAreas = React.useMemo(
    () => new Set(progressive.neededAreaKeys || []),
    [progressive.neededAreaKeys],
  );
  const secondaryVisible = progressiveEnabled && progressive.expanded;
  const visiblePrimary = progressive.primary;
  const visibleSecondary = secondaryVisible ? progressive.secondary : [];
  const hasSecondary = progressiveEnabled && progressive.secondary.length > 0;

  if (!areas.length) return null;

  const goArea = (area) => {
    navigate(resolveDashboardAreaNavigationTarget(navigation, area, location));
  };

  const toggleSecondary = () => {
    setProgressiveState((current) =>
      setProjectNavigationExpanded(project, !progressive.expanded, current, user),
    );
  };

  const renderArea = (area, { secondary = false } = {}) => {
    const recommended = neededAreas.has(area.key);
    const lockDetail = area.lockLabel || "temporalmente bloqueada";
    const tooltipBase = area.locked ? `${area.label} · ${lockDetail}` : area.label;
    const tooltip = recommended && !area.active
      ? `${tooltipBase} · recomendado para tu puesta en marcha`
      : tooltipBase;

    return (
      <Tooltip key={area.key} title={tooltip} arrow enterDelay={350}>
        <IconButton
          type="button"
          aria-label={`Ir a ${area.label}${area.locked ? `, ${lockDetail}` : ""}${recommended ? ", recomendado para tu puesta en marcha" : ""}`}
          aria-current={area.active ? "page" : undefined}
          data-navigation-priority={recommended ? "guided" : (secondary ? "secondary" : "primary")}
          onClick={() => goArea(area)}
          sx={{
            position: "relative",
            width: 42,
            height: 42,
            borderRadius: 2.25,
            color: area.active ? "#fff" : secondary ? "rgba(var(--wp-panel-ink-rgb, 25,36,47),.48)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
            bgcolor: area.active ? ACTIVE : "transparent",
            opacity: secondary && !area.active ? 0.78 : 1,
            boxShadow: area.active
              ? "0 8px 22px rgba(243,90,58,.26), inset 0 0 0 1px rgba(255,255,255,.14)"
              : "none",
            transition: "background-color 140ms ease, color 140ms ease, transform 140ms ease, opacity 140ms ease, box-shadow 180ms ease",
            "@keyframes wpTopNavActivePop": {
              "0%": { transform: "scale(.9)", boxShadow: "0 0 0 rgba(243,90,58,0)" },
              "58%": { transform: "scale(1.1)", boxShadow: "0 10px 28px rgba(243,90,58,.32)" },
              "100%": { transform: "scale(1)", boxShadow: "0 8px 22px rgba(243,90,58,.26)" },
            },
            "@keyframes wpTopNavShine": {
              "0%": { opacity: 0, backgroundPosition: "140% 0" },
              "28%": { opacity: 0.95 },
              "100%": { opacity: 0, backgroundPosition: "-140% 0" },
            },
            animation: area.active ? "wpTopNavActivePop 480ms cubic-bezier(.2,.9,.25,1.2)" : "none",
            "&::before": area.active
              ? {
                  content: '""',
                  position: "absolute",
                  inset: 2,
                  borderRadius: "inherit",
                  pointerEvents: "none",
                  background: "linear-gradient(115deg, transparent 28%, rgba(255,255,255,.78) 49%, transparent 70%)",
                  backgroundSize: "250% 100%",
                  opacity: 0,
                  animation: "wpTopNavShine 680ms ease-out 70ms 1",
                }
              : undefined,
            "&:hover": {
              bgcolor: area.active ? ACTIVE : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
              color: area.active ? "#fff" : INK,
              opacity: 1,
              transform: "translateY(-1px)",
            },
            "&.Mui-focusVisible": {
              outline: `3px solid ${area.active ? "rgba(243,90,58,.28)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.18)"}`,
              outlineOffset: 2,
            },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              transitionDuration: "0ms",
              "&::before": { animation: "none" },
            },
            "&::after": area.active
              ? {
                  content: '\"\"',
                  position: "absolute",
                  left: "50%",
                  bottom: -5,
                  width: 16,
                  height: 3,
                  borderRadius: 999,
                  bgcolor: ACTIVE,
                  transform: "translateX(-50%)",
                }
              : undefined,
          }}
        >
          {area.locked ? (
            <LockClockRoundedIcon sx={{ fontSize: 21 }} />
          ) : (
            <DashboardNavigationIcon name={area.iconKey} sx={{ fontSize: 22 }} />
          )}
          {recommended && !area.active ? (
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                position: "absolute",
                top: 5,
                right: 5,
                width: 7,
                height: 7,
                borderRadius: "50%",
                bgcolor: ACTIVE,
                boxShadow: "0 0 0 2px rgba(var(--wp-panel-surface-rgb, 255,255,255),.94)",
              }}
            />
          ) : null}
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box
      component="nav"
      data-wp-tutorial-target="panel-primary-navigation"
      aria-label="Áreas principales del panel"
      sx={{
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          width: "max-content",
          mx: "auto",
          p: 0.5,
          borderRadius: 3,
          bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.045)",
          border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)",
        }}
      >
        {visiblePrimary.map((area) => renderArea(area))}

        {visibleSecondary.length ? (
          <>
            <Box
              aria-hidden="true"
              sx={{ width: 1, height: 26, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.1)", mx: 0.2 }}
            />
            {visibleSecondary.map((area) => renderArea(area, { secondary: true }))}
          </>
        ) : null}

        {hasSecondary ? (
          <Tooltip
            title={progressive.expanded ? "Ocultar herramientas secundarias" : "Mostrar más herramientas"}
            arrow
          >
            <Button
              type="button"
              size="small"
              aria-expanded={progressive.expanded ? "true" : "false"}
              onClick={toggleSecondary}
              startIcon={progressive.expanded ? <ExpandLessRoundedIcon /> : <MoreHorizRoundedIcon />}
              sx={{
                minHeight: 36,
                ml: 0.25,
                px: 1.1,
                borderRadius: 2,
                textTransform: "none",
                color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)",
                fontSize: ".72rem",
                fontWeight: 900,
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)", color: INK },
              }}
            >
              {progressive.expanded ? "Ocultar" : "Más herramientas"}
            </Button>
          </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
}
